"use client";

import { useCallback, useEffect, useState } from "react";
import {
  AnimatePresence,
  motion,
  useAnimationFrame,
  useMotionTemplate,
  useMotionValue,
  useTransform,
} from "framer-motion";
import {
  Apple,
  ArrowRight,
  ArrowUp,
  AudioLines,
  ChevronLeft,
  Coins,
  Fingerprint,
  Ghost,
  Github,
  Mail,
  MessageCircle,
  Mic,
  Plus,
  ShieldCheck,
  Twitter,
  Wallet,
  WalletMinimal,
  Languages,
  Loader2,
  Settings2,
  HelpCircle,
  Pause,
} from "lucide-react";
import { useMemo, useRef, type ReactNode, JSX } from "react";
import useMeasure from "react-use-measure";
import { Drawer } from "vaul";
import { MeshGradient } from "@paper-design/shaders-react";
import useSound from "use-sound";

import { cn } from "@/lib/utils";

import { InputOTP, InputOTPSlot } from "@/components/input-otp";
import { AudioLinesIcon, type AudioLinesIconHandle } from "@/components/audio-lines";
import { MemoizedMarkdown } from "@/components/ui/memoized-markdown";
import { TextGenerateEffect } from "@/components/text-generate-effect";
import { toast } from "sonner";
import { useUser } from "@/lib/context/user-context";
import { useUsage } from "@/contexts/usage-context";
import { Switch } from "@/components/ui/switch";
import { supabase } from "@/core/supabase";

// 100ms 静音音频，用于在用户手势中预热音频播放权限
const SILENT_WAV_DATA_URI =
  'data:audio/wav;base64,UklGRmQGAABXQVZFZm10IBAAAAABAAEAQB8AAIA+AAACABAAZGF0YUAGAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA';
// import { Button } from "@/components/ui/button";

const socialProviders = [
  { icon: Mail, label: "Sign in with Google" },
  { icon: MessageCircle, label: "Sign in with Discord" },
  { icon: Github, label: "Sign in with GitHub" },
  { icon: Apple, label: "Sign in with Apple" },
  { icon: Twitter, label: "Sign in with Farcaster" },
];

const authMethods = ["Email", "Phone", "Passkey"];

const walletProviders = [
  { icon: WalletMinimal, label: "Metamask" },
  { icon: Coins, label: "Coinbase" },
  { icon: Ghost, label: "Phantom" },
  { icon: ShieldCheck, label: "Trust Wallet" },
];

interface Flashcard {
  id: string;
  front: string;
  back: string;
  context?: string | null;
  tips?: string | null;
  practiceType?: string;
  term?: string | null;
  createdAt: string;
}

interface Message {
  id?: string;
  sentByMe: boolean;
  message: string;
  audioUrl?: string;
  generatedAt?: number;
  audioReadyAt?: number;
  flashcard?: Flashcard;
}

interface TtsSession {
  messageId: string;
  mediaSource: MediaSource;
  objectUrl: string;
  chunkQueue: Uint8Array[];
  recordedChunks: Uint8Array[];
  sourceBuffer?: SourceBuffer;
  shouldClose: boolean;
  tailPromise: Promise<void>;
}

// TextShimmer component
const TextShimmer = ({
  children,
  className,
  duration = 2,
}: {
  children: React.ReactNode;
  className?: string;
  duration?: number;
}) => {
  return (
    <motion.span
      className={cn("relative inline-block", className)}
      animate={{
        backgroundPosition: ["200% 0", "-200% 0"],
      }}
      transition={{
        duration,
        repeat: Infinity,
        ease: "linear",
      }}
      style={{
        background: "linear-gradient(90deg, #121212 0%, #121212 50%, #121212 100%)",
        backgroundSize: "200% 100%",
        WebkitBackgroundClip: "text",
        WebkitTextFillColor: "transparent",
        backgroundClip: "text",
      }}
    >
      {children}
    </motion.span>
  );
};

const PlayTriangleIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path d="M8 5l10 7-10 7V5z" />
  </svg>
);

function ScenarioPracticeIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" {...props}>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M12.5 3.444a1 1 0 0 0-1 0l-6.253 3.61 6.768 3.807 6.955-3.682-6.47-3.735Zm7.16 5.632L13 12.602v7.666l6.16-3.556a1 1 0 0 0 .5-.867V9.076ZM11 20.268v-7.683L4.34 8.839v7.006a1 1 0 0 0 .5.867L11 20.268Zm-.5-18.557a3 3 0 0 1 3 0l6.66 3.846a3 3 0 0 1 1.5 2.598v7.69a3 3 0 0 1-1.5 2.598L13.5 22.29a3 3 0 0 1-3 0l-6.66-3.846a3 3 0 0 1-1.5-2.598v-7.69a3 3 0 0 1 1.5-2.598L10.5 1.71Z"
        stroke="currentColor"
        strokeWidth={1.2}
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ShadowingComparisonIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" {...props}>
      <path
        d="M12 3c3.585 0 6.5 2.923 6.5 6.538A6.542 6.542 0 0 1 15.575 15h-7.15A6.542 6.542 0 0 1 5.5 9.538C5.5 5.923 8.415 3 12 3Zm2.865 14v1h-5.73v-1h5.73Zm-1.133 3a2 2 0 0 1-3.464 0h3.464Zm-5.606 0a4.002 4.002 0 0 0 7.748 0 1 1 0 0 0 .991-1v-2.46A8.54 8.54 0 0 0 20.5 9.539C20.5 4.828 16.7 1 12 1S3.5 4.828 3.5 9.538a8.54 8.54 0 0 0 3.635 7.003V19a1 1 0 0 0 .991 1Z"
        stroke="currentColor"
        strokeWidth={1.2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const quickChatActions = [
  {
    label: "口语对话",
    prompt: "我想练习口语对话",
    icon: ScenarioPracticeIcon,
    accentClassName: "text-yellow-500",
  },
  {
    label: "地道表达",
    prompt: "我想练习地道表达",
    icon: ShadowingComparisonIcon,
    accentClassName: "text-rose-500",
  },
];

interface Skiper21Props {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  hideTriggerButton?: boolean;
  vocabularyData?: any; // Vocabulary data from episode
  initialTerm?: string; // Term to practice directly (skip welcome message)
  initialPracticeType?: 'speaking' | 'idiomatic'; // Type of practice to start
}

export const Skiper21 = ({
  open,
  onOpenChange,
  hideTriggerButton = false,
  vocabularyData,
  initialTerm,
  initialPracticeType,
}: Skiper21Props) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = typeof open === "boolean";
  const resolvedOpen = isControlled ? (open as boolean) : internalOpen;

  const handleOpenChange = (nextOpen: boolean) => {
    if (!isControlled) {
      setInternalOpen(nextOpen);
    }
    onOpenChange?.(nextOpen);
  };

  const [view, setView] = useState("signin");
  const [elementRef, bounds] = useMeasure();

  useEffect(() => {
    if (!resolvedOpen) {
      setView("signin");
    }
  }, [resolvedOpen]);

  // Use a ref to track if the drawer was closed
  const drawerOpenCountRef = useRef(0);
  
  useEffect(() => {
    if (resolvedOpen) {
      drawerOpenCountRef.current += 1;
    }
  }, [resolvedOpen]);

  const content = useMemo(() => {
    switch (view) {
      case "signin":
        // Add key to force remount only when drawer is reopened (not on every state change)
        return <SignInView 
          key={`signin-${drawerOpenCountRef.current}`} 
          setView={setView} 
          vocabularyData={vocabularyData}
          initialTerm={initialTerm}
          initialPracticeType={initialPracticeType}
        />;
      case "email-otp":
        return <EmailOTPView setView={setView} />;
      case "phone-otp":
        return <PhoneOTPView setView={setView} />;
      case "passkey":
        return <PasskeyView setView={setView} />;
      case "wallet":
        return <WalletView setView={setView} />;
    }
  }, [view, vocabularyData, initialTerm, initialPracticeType]);

  const triggerButton = hideTriggerButton ? null : (
    <div className="flex h-full w-full flex-col items-center justify-center">
      <button
        onClick={() => handleOpenChange(true)}
        className="rounded-full border border-border bg-white px-4 py-2 text-sm font-semibold text-[#222] shadow-[0_10px_28px_-24px_rgba(15,23,42,0.65)] transition-transform hover:-translate-y-[1px] hover:shadow-[0_12px_32px_-26px_rgba(15,23,42,0.65)]"
      >
        Sign In
      </button>
    </div>
  );

  const drawer = (
    <Drawer.Root open={resolvedOpen} onOpenChange={handleOpenChange}>
      <Drawer.Portal>
        <Drawer.Overlay
          className="bg-transparent fixed inset-0 z-[60]"
          onClick={() => handleOpenChange(false)}
        />
        <Drawer.Content
          className="bg-white border border-border z-[61] fixed inset-x-4 bottom-4 mx-auto max-w-[361px] overflow-hidden rounded-[28px] shadow-[0_30px_60px_-40px_rgba(15,23,42,0.45)] outline-none md:mx-auto md:w-full"
        >
          <Drawer.Title className="hidden">Skiper ui </Drawer.Title>
          <motion.div
            animate={{
              height: bounds.height,
              transition: {
                duration: 0.27,
                ease: [0.25, 1, 0.5, 1],
              },
            }}
          >
            <Drawer.Close asChild>
              <button className="absolute right-3 top-2 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-white text-[#777] shadow-[0_6px_18px_-12px_rgba(15,23,42,0.35)] transition-transform focus:scale-95 active:scale-90">
                <Plus className="rotate-45" />
              </button>
            </Drawer.Close>
            <div ref={elementRef} className="bg-muted">
              <AnimatePresence initial={false} mode="popLayout" custom={view}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.96 }}
                  key={view}
                  transition={{
                    duration: 0.27,
                    ease: [0.26, 0.08, 0.25, 1],
                  }}
                >
                  {content}
                </motion.div>
              </AnimatePresence>
            </div>
          </motion.div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );

  if (hideTriggerButton) {
    return drawer;
  }

  return (
    <div className="h-full w-full">
      {triggerButton}
      {drawer}
    </div>
  );
};

interface Skiper21DrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
  surfaceClassName?: string;
  bodyClassName?: string;
  variant?: "default" | "bare";
  showCloseButton?: boolean;
}

export const Skiper21Drawer: React.FC<Skiper21DrawerProps> = ({
  open,
  onOpenChange,
  children,
  surfaceClassName,
  bodyClassName,
  variant = "default",
  showCloseButton,
}) => {
  const isBare = variant === "bare";
  const shouldShowCloseButton =
    typeof showCloseButton === "boolean" ? showCloseButton : !isBare;

  return (
    <Drawer.Root open={open} onOpenChange={onOpenChange}>
      <Drawer.Portal>
        <Drawer.Overlay
          className="bg-transparent fixed inset-0 z-[60]"
          onClick={(e) => {
            // Only close if clicking directly on overlay, not on child elements
            if (e.target === e.currentTarget) {
              onOpenChange(false);
            }
          }}
        />
        <Drawer.Content
          className={cn(
            "z-[61] fixed inset-x-4 bottom-4 mx-auto max-w-[361px] outline-none md:mx-auto md:w-full",
            isBare
              ? "bg-transparent overflow-hidden rounded-[40px] border border-transparent shadow-none"
              : "bg-white overflow-hidden rounded-[32px] border border-[#eceef2] shadow-[0_30px_60px_-40px_rgba(15,23,42,0.45)]",
            surfaceClassName
          )}
        >
          <Drawer.Title className="hidden">Skiper drawer</Drawer.Title>
          {shouldShowCloseButton ? (
            <Drawer.Close asChild>
              <button className="absolute right-3 top-2 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-white text-[#777] shadow-[0_6px_18px_-12px_rgba(15,23,42,0.35)] transition-transform focus:scale-95 active:scale-90">
                <Plus className="rotate-45" />
              </button>
            </Drawer.Close>
          ) : null}
          <div
            className={cn(isBare ? "bg-transparent" : "bg-muted", bodyClassName)}
          >
            {children}
          </div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
};

// Skiper84 component
const Skiper84 = () => {
  const [ref, bounds] = useMeasure();

  const [inputValue, setInputValue] = useState("");

  const onSubmit = () => {
    setInputValue("");
    setIsSubmit(true);
  };

  const [isSubmit, setIsSubmit] = useState(false);

  return (
    <div className="perspective-near relative flex h-full w-screen items-center justify-center px-3 text-[#121212]">
      <AnimatePresence mode="popLayout">
        {isSubmit ? (
          <motion.div
            key="output"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="absolute z-20 flex h-full w-full items-center justify-center text-center"
          >
            <TextShimmer className="text-lg" duration={1}>
              Generating Your $Billion Dollar saas...
            </TextShimmer>
          </motion.div>
        ) : (
          <motion.div
            key="input"
            exit={{ y: -100, opacity: 0, filter: "blur(4px)", rotateX: 25 }}
            animate={{ y: 0, opacity: 1, rotateX: 0 }}
            transition={{ duration: 0.3 }}
            layout
            className="z-2 absolute w-full max-w-sm rounded-3xl bg-white text-lg"
          >
            <div className="flex justify-between p-2">
              <div className="flex w-full items-center gap-1">
                <button className="absolute bottom-2 left-2 flex size-11 items-center justify-center rounded-full p-1">
                  <Plus className="text-muted-foreground size-5" />
                </button>
                <motion.div
                  className="w-full"
                  initial={false}
                  animate={{
                    paddingLeft: inputValue.length > 10 ? "15px" : "38px",
                    paddingRight: inputValue.length > 10 ? "15px" : "82px",
                    height: bounds.height || "auto",
                    marginBottom: inputValue.length > 10 ? "40px" : "0px",
                  }}
                >
                  <textarea
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        onSubmit();
                      }
                    }}
                    placeholder="Ask anything..."
                    ref={ref}
                    className={cn(
                      "field-sizing-content w-full resize-none rounded-md py-2 outline-0 focus-visible:ring-0",
                    )}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                  />
                </motion.div>
              </div>
              {isSubmit}
              <div className="absolute bottom-2 right-2 flex">
                <button className="flex size-11 items-center justify-center rounded-full p-1">
                  <Mic className="size-5" />
                </button>

                {inputValue ? (
                  <motion.button
                    initial={{ scale: 0.5 }}
                    animate={{ scale: 1 }}
                    key="arrow-up"
                    className="flex size-11 items-center justify-center rounded-full bg-[#121212] p-1 text-white"
                  >
                    <ArrowUp className="size-5" />
                  </motion.button>
                ) : (
                  <motion.button
                    initial={{ scale: 0.5 }}
                    animate={{ scale: 1 }}
                    key="audio-lines"
                    className="flex size-11 items-center justify-center rounded-full bg-[#121212] p-1 text-white"
                  >
                    <AudioLines className="size-5" />
                  </motion.button>
                )}
        </div>
      </div>
          </motion.div>
        )}
      </AnimatePresence>

      <MeshGradient
        speed={1}
        colors={["#C1DAFE", "#96BEFF", "#CFB7FC", "#EBE1F9"]}
        distortion={0.8}
        swirl={1}
        grainMixer={0}
        grainOverlay={0}
        style={{
          height: "100%",
          width: "100%",
          position: "absolute",
        }}
      />
    </div>
  );
};

// Grad component for background effect
const Grad = ({ className }: { className?: string }) => (
  <div
    className={cn("flex h-full flex-col items-stretch -space-y-3", className)}
  >
    <div className="w-full flex-1 bg-[#FC2BA3] blur-xl" />
    <div className="w-full flex-1 bg-[#FC6D35] blur-xl" />
    <div className="w-full flex-1 bg-[#F9C83D] blur-xl" />
    <div className="w-full flex-1 bg-[#C2D6E1] blur-xl" />
    <div className="w-full flex-1 bg-[#144EC5] blur-xl" />
  </div>
);

// Helper function to extract a random term from vocabulary
function getRandomVocabularyTerm(vocabularyData: any): {
  term: string;
  explanation?: string;
  example?: { english?: string; translation?: string };
  scenarioName?: string;
} | null {
  if (!vocabularyData?.scenarios || !Array.isArray(vocabularyData.scenarios)) {
    return null;
  }

  // Collect all terms from all scenarios
  const allTerms: Array<{
    term: string;
    explanation?: string;
    example?: { english?: string; translation?: string };
    scenarioName?: string;
  }> = [];

  vocabularyData.scenarios.forEach((scenario: any) => {
    if (Array.isArray(scenario?.terms)) {
      scenario.terms.forEach((term: any) => {
        if (term?.term) {
          const example = Array.isArray(term.examples) && term.examples.length > 0
            ? term.examples[0]
            : undefined;
          
          allTerms.push({
            term: term.term,
            explanation: term.explanation || term.chosen_explanation,
            example: example ? {
              english: example.english,
              translation: example.translation,
            } : undefined,
            scenarioName: scenario?.name,
          });
        }
      });
    }
  });

  if (allTerms.length === 0) {
    return null;
  }

  // Return a random term
  const randomIndex = Math.floor(Math.random() * allTerms.length);
  return allTerms[randomIndex];
}

// Flashcard Component
function FlashcardComponent({ flashcard }: { flashcard: Flashcard }) {
  const [showTranslation, setShowTranslation] = useState(false);

  // Format date from createdAt
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric' 
      });
    } catch {
      return 'Today';
    }
  };

  // Get the main term (prefer term, fallback to front)
  const mainTerm = flashcard.term || flashcard.front;
  // Get the subtitle (if term exists, front is the subtitle/pronunciation)
  const subtitle = flashcard.term ? flashcard.front : undefined;

  return (
    <div className="mt-3 w-full max-w-[320px] mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
        className="relative w-full bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden"
      >
        {/* Card Content */}
        <div className="p-5 flex flex-col">
          {/* Header: Term and Badge */}
          <div className="mb-3">
            <div className="flex items-center gap-3 mb-1">
              <h3 className="text-2xl font-semibold text-black leading-tight">
                {mainTerm}
              </h3>
              {flashcard.practiceType && (
                <span className="px-2.5 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs font-medium whitespace-nowrap">
                  {flashcard.practiceType === 'vocabulary' ? '词汇' : 
                   flashcard.practiceType === 'conversation' ? '对话' :
                   flashcard.practiceType === 'idiomatic' ? '地道表达' : 'New'}
                </span>
              )}
            </div>
            {subtitle && (
              <p className="text-sm text-gray-600 mt-0.5">
                {subtitle}
              </p>
            )}
          </div>

          {/* Divider */}
          <div className="h-px bg-gray-200 mb-4"></div>

          {/* Translation Section */}
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-2">
              <HelpCircle className="h-4 w-4 text-gray-400 flex-shrink-0" />
              <span className="text-sm text-gray-600">
                {showTranslation ? 'Translation' : 'Tap Reveal to see translation'}
              </span>
            </div>
            
            {showTranslation && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2 }}
                className="text-base text-gray-800 mb-3"
              >
                {flashcard.back}
              </motion.div>
            )}

            {/* Example Sentence */}
            {flashcard.context && (
              <div className="text-sm text-gray-700 italic mb-3">
                "{flashcard.context}"
              </div>
            )}
          </div>

          {/* Reveal Button */}
          <button
            onClick={() => setShowTranslation(!showTranslation)}
            className="w-full py-2 px-4 bg-black text-white rounded-xl font-medium text-base hover:bg-gray-800 active:bg-gray-900 transition-colors mb-4 shadow-sm"
          >
            {showTranslation ? 'Hide Translation' : 'Reveal'}
          </button>

          {/* Tips Section */}
          {flashcard.tips && showTranslation && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              transition={{ duration: 0.3 }}
              className="mt-2 p-3 bg-amber-50 rounded-lg border border-amber-200"
            >
              <div className="flex items-start gap-2">
                <span className="text-lg flex-shrink-0">💡</span>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-amber-900 mb-1">记忆提示</div>
                  <div className="text-sm text-amber-800 leading-relaxed">
                    {flashcard.tips}
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Footer */}
          <div className="mt-4 pt-3 border-t border-gray-200 flex items-center justify-between text-xs text-gray-500">
            <span>
              Added {formatDate(flashcard.createdAt)}
            </span>
            <span>
              Source: {flashcard.practiceType || 'original'}
            </span>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

// Sign In View (Simplified with messages)
function SignInView({ 
  setView, 
  vocabularyData,
  initialTerm,
  initialPracticeType,
}: { 
  setView: (view: string) => void; 
  vocabularyData?: any;
  initialTerm?: string;
  initialPracticeType?: 'speaking' | 'idiomatic';
}) {
  const [input, setInput] = useState<string>("");
  const [isSubmit, setIsSubmit] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isRecording, setIsRecording] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioLinesRef = useRef<AudioLinesIconHandle>(null);
  const settingsButtonRef = useRef<HTMLButtonElement | null>(null);
  const settingsMenuRef = useRef<HTMLDivElement | null>(null);
  const audioRefs = useRef<Map<string, HTMLAudioElement>>(new Map());
  const playedAudioRef = useRef<Set<string>>(new Set());
  const playbackQueueRef = useRef<string[]>([]);
  const isPlayingRef = useRef(false);
  const currentAudioRef = useRef<HTMLAudioElement | null>(null);
  const manualAudioMapRef = useRef<Map<string, string>>(new Map());
  const pendingStaticAudioRef = useRef<Map<string, { blobUrl: string; objectUrl: string }>>(new Map());
  const ttsSessionsRef = useRef<Map<string, TtsSession>>(new Map());
  const bufferedTtsTextRef = useRef<Map<string, string>>(new Map());
  const welcomeInitializedRef = useRef(false);
  const audioUnlockedRef = useRef(false);
  const primedAudioElementRef = useRef<HTMLAudioElement | null>(null); // 在用户交互时预热的音频元素
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  const [audioUnlockTimestamp, setAudioUnlockTimestamp] = useState<number | null>(Date.now());
  const [isSettingsMenuOpen, setIsSettingsMenuOpen] = useState(false);
  const [play] = useSound("/sounds/send.mp3", {
    volume: 0.2,
  });
  const { user } = useUser();
  const { usageInfo, decrementChats, refreshUsage } = useUsage();
  const isPremiumUser = usageInfo?.isPremium === true || user?.user_metadata?.isPremium === true;
  const [translationState, setTranslationState] = useState<Record<
    string,
    { text?: string; loading?: boolean; error?: string }
  >>({});
  const [playingMessageIds, setPlayingMessageIds] = useState<Set<string>>(() => new Set());

  const addPlayingMessage = useCallback((messageId: string) => {
    if (!messageId) {
      return;
    }
    setPlayingMessageIds((prev) => {
      if (prev.has(messageId)) {
        return prev;
      }
      const next = new Set(prev);
      next.add(messageId);
      return next;
    });
  }, []);

  const removePlayingMessage = useCallback((messageId: string) => {
    if (!messageId) {
      return;
    }
    setPlayingMessageIds((prev) => {
      if (!prev.has(messageId)) {
        return prev;
      }
      const next = new Set(prev);
      next.delete(messageId);
      return next;
    });
  }, []);

  const clearPlayingMessages = useCallback(() => {
    setPlayingMessageIds((prev) => {
      if (prev.size === 0) {
        return prev;
      }
      return new Set<string>();
    });
  }, []);

  const showQuickChatActions = useMemo(
    () => messages.some((msg) => msg.id === "start"),
    [messages],
  );

  // Optional: tema and nivel for personalized welcome message
  const tema = 'general conversation'; // Can be made configurable
  const nivel = 'intermediate'; // Can be made configurable

  useEffect(() => {
    if (!isSettingsMenuOpen) {
      return;
    }
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (
        settingsMenuRef.current?.contains(target) ||
        settingsButtonRef.current?.contains(target)
      ) {
        return;
      }
      setIsSettingsMenuOpen(false);
    };
    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [isSettingsMenuOpen]);

  // Initialize welcome message
  useEffect(() => {
    // Prevent double execution in React Strict Mode
    if (welcomeInitializedRef.current) {
      return;
    }
    welcomeInitializedRef.current = true;

    // If initialTerm is provided, skip welcome message
    if (initialTerm && initialPracticeType === 'speaking') {
      // Don't show welcome message, will start practice directly in separate effect
      return;
    }

    const welcomeMessage: Message = {
      id: 'start',
      sentByMe: false,
      message: 'Hi there! How can I help you?',
      // message: 'Hi there! Which one do you want to practice, "口语对话"还是"地道表达"? I am here to help you!',
      generatedAt: Date.now(),
      // message: `Hi there! I'm excited to help you with ${tema} at a ${nivel} level. Let's get started! How are you today?`,
    };

    // Display welcome message immediately
    setMessages([welcomeMessage]);

    // Generate TTS for welcome message in the background
    setTimeout(async () => {
      try {
        const ttsResponse = await fetch('/api/tts-openai', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            text: welcomeMessage.message,
            voice: 'coral',
            speed: 1.0,
            instructions: "Speak in a friendly tone, like a friend would.",
          }),
        });

        if (ttsResponse.ok) {
          const audioBlob = await ttsResponse.blob();
          const audioUrl = URL.createObjectURL(audioBlob);
          
          // Update the message by mapping instead of replacing the array
          const readyAt = Date.now();
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === 'start'
                ? { ...msg, audioUrl, audioReadyAt: msg.audioReadyAt ?? readyAt }
                : msg
            )
          );
        }
      } catch (error) {
        console.error('[Chat] TTS error for welcome message:', error);
      }
    }, 100); // Small delay to ensure message displays first
  }, [initialTerm, initialPracticeType]); // Only run once on mount

  // Start practice directly if initialTerm is provided
  // This will be handled after handleMessageSubmit is defined
  const practiceStartedRef = useRef(false);
  const initialTermRef = useRef(initialTerm);
  const initialPracticeTypeRef = useRef(initialPracticeType);
  
  useEffect(() => {
    initialTermRef.current = initialTerm;
    initialPracticeTypeRef.current = initialPracticeType;
  }, [initialTerm, initialPracticeType]);

  // Auto scroll to bottom when new messages are added
  useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop =
        messagesContainerRef.current.scrollHeight;
    }
  }, [messages]);

  // Cleanup media recorder
  const cleanupMediaRecorder = useCallback(() => {
    if (mediaRecorderRef.current) {
      if (mediaRecorderRef.current.state !== "inactive") {
        try {
          mediaRecorderRef.current.stop();
        } catch (e) {
          // Ignore errors when stopping
        }
      }
      mediaRecorderRef.current = null;
    }
    setIsRecording(false);
    audioLinesRef.current?.stopAnimation();
  }, []);

  // Cleanup on unmount only (not on messages update)
  useEffect(() => {
    return () => {
      cleanupMediaRecorder();
      // Cleanup audio URLs to prevent memory leaks
      // Access current messages from ref instead of closure
      const currentMessages = messagesEndRef.current?.parentElement?.querySelectorAll('audio');
      currentMessages?.forEach((audio: HTMLAudioElement) => {
        if (audio.src && audio.src.startsWith('blob:')) {
          URL.revokeObjectURL(audio.src);
        }
      });
      // Clear played audio tracking
      playedAudioRef.current.clear();
      audioRefs.current.clear();
      ttsSessionsRef.current.forEach((session) => {
        try {
          if (session.mediaSource.readyState === "open") {
            session.mediaSource.endOfStream();
          }
        } catch {
          // ignore errors during cleanup
        }
        URL.revokeObjectURL(session.objectUrl);
      });
      ttsSessionsRef.current.clear();
      bufferedTtsTextRef.current.clear();
      playbackQueueRef.current = [];
      isPlayingRef.current = false;
      if (currentAudioRef.current) {
        currentAudioRef.current.pause();
        currentAudioRef.current = null;
      }
      manualAudioMapRef.current.forEach((url) => URL.revokeObjectURL(url));
      manualAudioMapRef.current.clear();
      pendingStaticAudioRef.current.forEach(({ objectUrl }) => {
        URL.revokeObjectURL(objectUrl);
      });
      pendingStaticAudioRef.current.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cleanupMediaRecorder]); // Remove messages from dependencies

  const flushTtsQueue = useCallback((session: TtsSession) => {
    if (!session.sourceBuffer || session.sourceBuffer.updating) {
      return;
    }

    if (session.chunkQueue.length > 0) {
      const nextChunk = session.chunkQueue.shift();
      if (nextChunk) {
        try {
          const { buffer, byteOffset, byteLength } = nextChunk;
          let arrayBuffer: ArrayBuffer;

          if (buffer instanceof ArrayBuffer) {
            arrayBuffer = buffer.slice(byteOffset, byteOffset + byteLength);
          } else {
            const copy = new Uint8Array(byteLength);
            copy.set(nextChunk);
            arrayBuffer = copy.buffer;
          }

          session.sourceBuffer.appendBuffer(arrayBuffer);
        } catch (error) {
          console.error("[Chat] Failed to append audio chunk:", error);
        }
      }
    } else if (session.shouldClose && session.mediaSource.readyState === "open") {
      try {
        session.mediaSource.endOfStream();
      } catch (error) {
        console.warn("[Chat] Failed to end MediaSource stream:", error);
      }
    }
  }, []);

  const finalizeTtsSession = useCallback(
    (session: TtsSession | undefined) => {
      if (!session) {
        return;
      }
      session.shouldClose = true;
      flushTtsQueue(session);
    },
    [flushTtsQueue],
  );

  const swapMessageAudioSource = useCallback(
    (messageId: string, nextUrl: string, objectUrlToRevoke?: string) => {
      if (!messageId) {
        if (objectUrlToRevoke) {
          URL.revokeObjectURL(objectUrlToRevoke);
        }
        return;
      }

      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === messageId && msg.audioUrl !== nextUrl
            ? {
                ...msg,
                audioUrl: nextUrl,
                audioReadyAt: msg.audioReadyAt ?? Date.now(),
              }
            : msg,
        ),
      );

      if (objectUrlToRevoke) {
        URL.revokeObjectURL(objectUrlToRevoke);
      }
    },
    [setMessages],
  );

  const convertSessionToStaticAudio = useCallback(
    (session: TtsSession | undefined) => {
      if (!session || session.recordedChunks.length === 0) {
        if (session) {
          ttsSessionsRef.current.delete(session.messageId);
        }
        return;
      }

      const totalLength = session.recordedChunks.reduce(
        (acc, chunk) => acc + chunk.byteLength,
        0,
      );
      const merged = new Uint8Array(totalLength);
      let offset = 0;
      session.recordedChunks.forEach((chunk) => {
        merged.set(chunk, offset);
        offset += chunk.byteLength;
      });

      const blob = new Blob([merged], { type: "audio/mpeg" });
      const blobUrl = URL.createObjectURL(blob);

      const previousUrl = manualAudioMapRef.current.get(session.messageId);
      if (previousUrl) {
        URL.revokeObjectURL(previousUrl);
      }
      manualAudioMapRef.current.set(session.messageId, blobUrl);

      const audioElement = audioRefs.current.get(session.messageId);
      const isCurrentlyPlaying =
        !!audioElement && currentAudioRef.current === audioElement && !audioElement.paused;
      const isQueued = playbackQueueRef.current.includes(session.messageId);

      if (isCurrentlyPlaying || isQueued) {
        pendingStaticAudioRef.current.set(session.messageId, {
          blobUrl,
          objectUrl: session.objectUrl,
        });
      } else {
        swapMessageAudioSource(session.messageId, blobUrl, session.objectUrl);
      }
      session.recordedChunks = [];
      ttsSessionsRef.current.delete(session.messageId);
    },
    [swapMessageAudioSource],
  );

  const getOrCreateTtsSession = useCallback(
    (messageId: string) => {
      let session = ttsSessionsRef.current.get(messageId);
      if (session) {
        return session;
      }

      const mediaSource = new MediaSource();
      const readyAt = Date.now();
      const objectUrl = URL.createObjectURL(mediaSource);
      session = {
        messageId,
        mediaSource,
        objectUrl,
        chunkQueue: [],
        recordedChunks: [],
        shouldClose: false,
        tailPromise: Promise.resolve(),
      };

      ttsSessionsRef.current.set(messageId, session);

      // 确保 audioReadyAt 使用当前时间，而不是消息的 generatedAt
      // 这样可以确保 audioReadyAt >= audioUnlockTimestamp（如果 audioUnlockTimestamp 是在按钮点击时设置的）
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === messageId && msg.audioUrl !== objectUrl
            ? {
                ...msg,
                audioUrl: objectUrl,
                audioReadyAt: readyAt, // 使用当前时间，确保 >= audioUnlockTimestamp
              }
            : msg,
        ),
      );

      mediaSource.addEventListener(
        "sourceopen",
        () => {
          const currentSession = ttsSessionsRef.current.get(messageId);
          if (!currentSession) {
            return;
          }

          try {
            currentSession.sourceBuffer = mediaSource.addSourceBuffer("audio/mpeg");
            currentSession.sourceBuffer.addEventListener("updateend", () =>
              flushTtsQueue(currentSession),
            );
            flushTtsQueue(currentSession);
          } catch (sourceError) {
            console.error("[Chat] Failed to initialize SourceBuffer:", sourceError);
            ttsSessionsRef.current.delete(messageId);
            URL.revokeObjectURL(objectUrl);
          }
        },
        { once: true },
      );

      return session;
    },
    [flushTtsQueue, setMessages],
  );

  const appendChunkToSession = useCallback(
    (session: TtsSession, chunk: Uint8Array) => {
      const chunkCopy =
        chunk instanceof Uint8Array ? chunk.slice() : new Uint8Array(chunk);
      session.recordedChunks.push(chunkCopy);
      session.chunkQueue.push(chunk);
      flushTtsQueue(session);
    },
    [flushTtsQueue],
  );

  const generateBufferedAudio = useCallback(
    async (text: string, messageId: string) => {
      if (!text.trim()) {
        return;
      }
      try {
        const response = await fetch("/api/tts-openai", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            text,
            voice: "alloy",
            speed: 1.0,
          }),
        });

        if (!response.ok) {
          throw new Error(`Buffered TTS request failed: ${response.status}`);
        }

        const audioBlob = await response.blob();
        const audioUrl = URL.createObjectURL(audioBlob);

        const previousUrl = manualAudioMapRef.current.get(messageId);
        if (previousUrl) {
          URL.revokeObjectURL(previousUrl);
        }
        manualAudioMapRef.current.set(messageId, audioUrl);

        swapMessageAudioSource(messageId, audioUrl);
      } catch (error) {
        console.error("[Chat] Buffered TTS error:", error);
      }
    },
    [swapMessageAudioSource],
  );

  const streamSegmentAudio = useCallback(
    async (session: TtsSession, text: string) => {
      const requestInit = () =>
        fetch("/api/tts-openai", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            text,
            voice: "alloy",
            speed: 1.0,
            instructions: "Read aloud in a warm, welcome and enthusiastic tone. In a slow and steady pace.",
          }),
        });

      const consumeResponse = async (response: Response) => {
        if (!response.ok) {
          throw new Error(`TTS request failed: ${response.status}`);
        }

        if (response.body) {
          const reader = response.body.getReader();
          try {
            while (true) {
              const { value, done } = await reader.read();
              if (value && value.length > 0) {
                const chunk = value instanceof Uint8Array ? value : new Uint8Array(value);
                appendChunkToSession(session, chunk);
              }
              if (done) {
                break;
              }
            }
          } finally {
            reader.releaseLock();
          }
        } else {
          const buffer = await response.arrayBuffer();
          appendChunkToSession(session, new Uint8Array(buffer));
        }
      };

      try {
        const response = await requestInit();
        await consumeResponse(response);
      } catch (error) {
        console.error("[Chat] Streaming TTS segment failed:", error);
        try {
          const fallbackResponse = await requestInit();
          await consumeResponse(fallbackResponse);
        } catch (fallbackError) {
          console.error("[Chat] Fallback TTS segment failed:", fallbackError);
        }
      }
    },
    [appendChunkToSession],
  );

  const streamTtsForMessage = useCallback(
    async (text: string, messageId: string, options?: { finalize?: boolean }) => {
      const trimmedText = text.trim();
      const supportsMediaSource =
        typeof window !== "undefined" && typeof window.MediaSource !== "undefined";

      if (!supportsMediaSource) {
        if (trimmedText) {
          const existing = bufferedTtsTextRef.current.get(messageId) ?? "";
          bufferedTtsTextRef.current.set(
            messageId,
            existing ? `${existing} ${trimmedText}` : trimmedText,
          );
        }

        if (options?.finalize) {
          const bufferedText = bufferedTtsTextRef.current.get(messageId);
          bufferedTtsTextRef.current.delete(messageId);
          if (bufferedText && bufferedText.trim()) {
            await generateBufferedAudio(bufferedText.trim(), messageId);
          }
        }
        return;
      }

      const session = getOrCreateTtsSession(messageId);

      if (trimmedText) {
        session.tailPromise = session.tailPromise
          .then(() => streamSegmentAudio(session, trimmedText))
          .catch((error) => {
            console.error("[Chat] Failed to queue TTS segment:", error);
          });
      }

      if (options?.finalize) {
        session.tailPromise = session.tailPromise
          .finally(() => finalizeTtsSession(session))
          .finally(() => convertSessionToStaticAudio(session));
      }
    },
    [convertSessionToStaticAudio, finalizeTtsSession, generateBufferedAudio, getOrCreateTtsSession, streamSegmentAudio],
  );

  // 预热音频播放权限（使用静音音频）
  const primeAudioPermission = useCallback(async (force = false) => {
    if (!force && audioUnlockedRef.current) {
      return true;
    }

    try {
      const audio = new Audio(SILENT_WAV_DATA_URI);
      audio.muted = true;
      audio.volume = 0;
      await audio.play();
      audio.pause();
      audio.currentTime = 0;
      audioUnlockedRef.current = true;
      return true;
    } catch (error) {
      console.warn('[Chat] Failed to prime audio permission:', error);
      audioUnlockedRef.current = false;
      return false;
    }
  }, []);

  const playNextAudioInQueue = useCallback(async () => {
    if (!isAudioEnabled || isPlayingRef.current) {
      console.log('[Chat] Skipping playback:', { isAudioEnabled, isPlaying: isPlayingRef.current });
      return;
    }

    while (playbackQueueRef.current.length > 0) {
      const messageKey = playbackQueueRef.current[0];
      const audioElement = audioRefs.current.get(messageKey);

      if (!audioElement) {
        console.log('[Chat] Audio element not ready, retrying:', messageKey);
        setTimeout(() => playNextAudioInQueue(), 150);
        return;
      }

      console.log('[Chat] Attempting to play audio:', messageKey, {
        src: audioElement.src,
        readyState: audioElement.readyState,
        networkState: audioElement.networkState,
      });

      // 等待音频元素准备好（至少要有元数据）
      // 如果还没准备好，等待加载完成后再尝试
      if (!audioElement.src || audioElement.readyState < HTMLMediaElement.HAVE_METADATA) {
        console.log('[Chat] Audio element not ready, waiting for metadata:', {
          hasSrc: !!audioElement.src,
          readyState: audioElement.readyState,
        });
        
        // 等待音频加载完成
        const waitForReady = new Promise<void>((resolve, reject) => {
          const timeout = setTimeout(() => {
            audioElement.removeEventListener('loadedmetadata', onLoadedMetadata);
            audioElement.removeEventListener('error', onError);
            reject(new Error('Audio load timeout'));
          }, 5000); // 5秒超时

          const onLoadedMetadata = () => {
            clearTimeout(timeout);
            audioElement.removeEventListener('loadedmetadata', onLoadedMetadata);
            audioElement.removeEventListener('error', onError);
            console.log('[Chat] Audio metadata loaded, ready to play');
            resolve();
          };

          const onError = (e: Event) => {
            clearTimeout(timeout);
            audioElement.removeEventListener('loadedmetadata', onLoadedMetadata);
            audioElement.removeEventListener('error', onError);
            reject(new Error('Audio load error'));
          };

          if (audioElement.readyState >= HTMLMediaElement.HAVE_METADATA) {
            clearTimeout(timeout);
            resolve();
          } else {
            audioElement.addEventListener('loadedmetadata', onLoadedMetadata, { once: true });
            audioElement.addEventListener('error', onError, { once: true });
          }
        });

        try {
          await waitForReady;
        } catch (error) {
          console.warn('[Chat] Failed to wait for audio ready:', error);
          // 如果等待失败，跳过这个音频，继续下一个
          playbackQueueRef.current.shift();
          playNextAudioInQueue();
          return;
        }
      }

      // 在实际播放之前，使用实际的音频元素来预热权限
      // 这比创建新的 Audio 对象更可靠，因为浏览器可能要求使用同一个音频元素
      try {
        // 检查音频元素是否已准备好（现在应该已经准备好了）
        const isReady = audioElement.src && 
                       (audioElement.readyState >= HTMLMediaElement.HAVE_METADATA || 
                        audioElement.readyState === HTMLMediaElement.HAVE_CURRENT_DATA);
        
        if (isReady) {
          // 先尝试使用实际的音频元素预热
          // 如果之前在用户交互时已经预热过，直接使用那个音频元素的权限
          if (primedAudioElementRef.current && audioUnlockedRef.current) {
            console.log('[Chat] Using pre-primed audio permission from user interaction');
            // 权限已经在用户交互时预热，可以直接播放
          } else {
            // 如果没有预热的音频元素，尝试使用实际音频元素预热
            const wasMuted = audioElement.muted;
            const oldVolume = audioElement.volume;
            audioElement.currentTime = 0;
            audioElement.muted = true;
            audioElement.volume = 0;
            try {
              await audioElement.play();
              audioElement.pause();
              audioElement.currentTime = 0;
              audioElement.muted = wasMuted;
              audioElement.volume = oldVolume;
              audioUnlockedRef.current = true;
              console.log('[Chat] Audio permission primed using actual audio element');
            } catch (playError) {
              // 如果使用实际音频元素失败，恢复设置并尝试使用静音音频
              audioElement.muted = wasMuted;
              audioElement.volume = oldVolume;
              console.warn('[Chat] Failed to prime using actual audio element, trying silent audio:', playError);
              const primed = await primeAudioPermission(true);
              if (!primed) {
                console.warn('[Chat] Failed to prime audio before playback');
                // 如果预热失败，跳过这个音频，继续下一个
                playbackQueueRef.current.shift();
                playNextAudioInQueue();
                return;
              }
            }
          }
        } else {
          // 如果音频元素还没准备好（这种情况不应该发生，因为我们已经等待了）
          console.warn('[Chat] Audio element still not ready after waiting, using silent audio to prime');
          const primed = await primeAudioPermission(true);
          if (!primed) {
            console.warn('[Chat] Failed to prime audio before playback');
            // 如果预热失败，跳过这个音频，继续下一个
            playbackQueueRef.current.shift();
            playNextAudioInQueue();
            return;
          }
        }
      } catch (error) {
        console.warn('[Chat] Failed to prime audio before playback:', error);
        // 如果预热失败，跳过这个音频，继续下一个
        playbackQueueRef.current.shift();
        playNextAudioInQueue();
        return;
      }

      audioElement.currentTime = 0;

      const handleEnded = () => {
        audioElement.removeEventListener("ended", handleEnded);
        playedAudioRef.current.add(messageKey);
        currentAudioRef.current = null;
        isPlayingRef.current = false;
        playbackQueueRef.current.shift();
        removePlayingMessage(messageKey);
        const pending = pendingStaticAudioRef.current.get(messageKey);
        if (pending) {
          pendingStaticAudioRef.current.delete(messageKey);
          swapMessageAudioSource(messageKey, pending.blobUrl, pending.objectUrl);
        }
        playNextAudioInQueue();
      };

      audioElement.addEventListener("ended", handleEnded, { once: true });

      const playPromise = audioElement.play();
      addPlayingMessage(messageKey);
      if (playPromise && typeof playPromise.then === "function") {
        isPlayingRef.current = true;
        currentAudioRef.current = audioElement;
        playPromise.catch(async (error) => {
          console.error("[Chat] Audio playback failed:", error);
          audioElement.removeEventListener("ended", handleEnded);
          currentAudioRef.current = null;
          isPlayingRef.current = false;
          playbackQueueRef.current.shift();
          removePlayingMessage(messageKey);

          if (error?.name === "NotAllowedError") {
            // 如果之前在用户交互时已经预热过，尝试使用预热的音频元素来播放实际音频
            if (primedAudioElementRef.current && audioUnlockedRef.current) {
              console.log('[Chat] Playback failed, trying to use pre-primed audio element for actual audio');
              try {
                // 尝试将实际音频的 src 复制到预热的音频元素
                const primedAudio = primedAudioElementRef.current;
                const actualSrc = audioElement.src;
                
                // 保存预热音频的当前状态
                const wasMuted = primedAudio.muted;
                const oldVolume = primedAudio.volume;
                
                // 设置预热的音频元素来播放实际音频
                primedAudio.src = actualSrc;
                primedAudio.muted = false;
                primedAudio.volume = 1;
                primedAudio.currentTime = 0;
                
                // 尝试播放
                await primedAudio.play();
                console.log('[Chat] Successfully playing audio using pre-primed audio element');
                
                // 监听播放结束事件
                const handlePrimedEnded = () => {
                  primedAudio.removeEventListener('ended', handlePrimedEnded);
                  playedAudioRef.current.add(messageKey);
                  currentAudioRef.current = null;
                  isPlayingRef.current = false;
                  playbackQueueRef.current.shift();
                  removePlayingMessage(messageKey);
                  
                  const pending = pendingStaticAudioRef.current.get(messageKey);
                  if (pending) {
                    pendingStaticAudioRef.current.delete(messageKey);
                    swapMessageAudioSource(messageKey, pending.blobUrl, pending.objectUrl);
                  }
                  
                  // 清理预热的音频元素，准备下次使用
                  primedAudio.pause();
                  primedAudio.currentTime = 0;
                  primedAudio.src = SILENT_WAV_DATA_URI;
                  primedAudio.muted = wasMuted;
                  primedAudio.volume = oldVolume;
                  
                  playNextAudioInQueue();
                };
                
                primedAudio.addEventListener('ended', handlePrimedEnded, { once: true });
                currentAudioRef.current = primedAudio;
                isPlayingRef.current = true;
                addPlayingMessage(messageKey);
                return; // 成功使用预热的音频元素播放，退出错误处理
              } catch (primedError) {
                console.warn('[Chat] Failed to use pre-primed audio element:', primedError);
                // 如果使用预热的音频元素也失败，继续下面的处理
              }
            }
            
            // 如果使用预热的音频元素失败，或者没有预热的音频元素
            // 不立即禁用音频，而是保持启用状态，等待用户的下一次交互
            // 这样用户下次点击按钮时，可以自动播放
            console.warn('[Chat] Audio playback blocked by browser, keeping audio enabled for next user interaction');
            audioUnlockedRef.current = false;
            // 不显示错误提示，因为音频仍然启用，下次用户交互时可以自动播放
            // setIsAudioEnabled(false);
            // toast.error("浏览器阻止了自动播放，请点击「开启语音」按钮重试。");
          } else {
            playedAudioRef.current.add(messageKey);
          }

          const pending = pendingStaticAudioRef.current.get(messageKey);
          if (pending) {
            pendingStaticAudioRef.current.delete(messageKey);
            swapMessageAudioSource(messageKey, pending.blobUrl, pending.objectUrl);
          }

          playNextAudioInQueue();
        });
        return;
      } else {
        audioElement.removeEventListener("ended", handleEnded);
        playedAudioRef.current.add(messageKey);
        playbackQueueRef.current.shift();
        removePlayingMessage(messageKey);
        const pending = pendingStaticAudioRef.current.get(messageKey);
        if (pending) {
          pendingStaticAudioRef.current.delete(messageKey);
          swapMessageAudioSource(messageKey, pending.blobUrl, pending.objectUrl);
        }
      }
    }
  }, [isAudioEnabled, swapMessageAudioSource, primeAudioPermission, addPlayingMessage, removePlayingMessage]);

  useEffect(() => {
    if (!isAudioEnabled || audioUnlockTimestamp === null) {
      return;
    }

    messages.forEach((msg, index) => {
      const messageKey = msg.id ?? `idx-${index}`;
      if (!messageKey) {
        return;
      }
      const readyAt = msg.audioReadyAt ?? msg.generatedAt ?? 0;
      const shouldAutoPlay = 
        !msg.sentByMe &&
        msg.audioUrl &&
        !playedAudioRef.current.has(messageKey) &&
        readyAt >= audioUnlockTimestamp &&
        !playbackQueueRef.current.includes(messageKey);
      
      if (shouldAutoPlay) {
        console.log('[Chat] Adding message to playback queue:', {
          messageKey,
          readyAt,
          audioUnlockTimestamp,
          hasAudioUrl: !!msg.audioUrl,
        });
        playbackQueueRef.current.push(messageKey);
      }
    });

    if (playbackQueueRef.current.length > 0) {
      console.log('[Chat] Playback queue:', playbackQueueRef.current);
      playNextAudioInQueue();
    }
  }, [messages, isAudioEnabled, audioUnlockTimestamp, playNextAudioInQueue]);

  useEffect(() => {
    if (!isAudioEnabled) {
      if (currentAudioRef.current) {
        currentAudioRef.current.pause();
        currentAudioRef.current.currentTime = 0;
        currentAudioRef.current = null;
      }
      isPlayingRef.current = false;
      playbackQueueRef.current = [];
      clearPlayingMessages();
    } else {
      playNextAudioInQueue();
    }
  }, [isAudioEnabled, playNextAudioInQueue, clearPlayingMessages]);

  const handleToggleAudioPlayback = useCallback(() => {
    setIsAudioEnabled((prev) => {
      const next = !prev;
      if (next) {
        setAudioUnlockTimestamp(Date.now());
        playbackQueueRef.current = [];
        playedAudioRef.current.clear();
        // 预热音频权限
        primeAudioPermission().catch(console.error);
        setTimeout(() => playNextAudioInQueue(), 0);
      } else {
        setAudioUnlockTimestamp(null);
        playbackQueueRef.current = [];
        if (currentAudioRef.current) {
          currentAudioRef.current.pause();
          currentAudioRef.current.currentTime = 0;
          currentAudioRef.current = null;
        }
        isPlayingRef.current = false;
        clearPlayingMessages();
      }
      return next;
    });
  }, [playNextAudioInQueue, primeAudioPermission, clearPlayingMessages]);

  const handleTranslateMessage = useCallback(
    async (messageId?: string, messageText?: string) => {
      if (!messageId || !messageText?.trim()) {
        toast.info("暂无可翻译的内容。");
        return;
      }

      setTranslationState((prev) => ({
        ...prev,
        [messageId]: { text: prev[messageId]?.text, loading: true, error: undefined },
      }));

      try {
        const response = await fetch("/api/chat/translate", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ text: messageText }),
        });

        if (!response.ok) {
          throw new Error(`翻译接口错误: ${response.statusText}`);
        }

        const data = await response.json();

        setTranslationState((prev) => ({
          ...prev,
          [messageId]: { text: data.translation ?? "", loading: false, error: undefined },
        }));
      } catch (error) {
        console.error("Translation error:", error);
        toast.error("翻译失败，请稍后再试。");
        setTranslationState((prev) => ({
          ...prev,
          [messageId]: { text: prev[messageId]?.text, loading: false, error: "翻译失败" },
        }));
      }
    },
    [],
  );

  const handleManualReplay = useCallback(
    async (messageId?: string) => {
      const key = messageId ?? "";
      const audioElement = audioRefs.current.get(key);
      if (!audioElement) {
        toast.info("音频尚未生成或已清理，稍后再试。");
        return;
      }

      let manualUrl = messageId ? manualAudioMapRef.current.get(messageId) : undefined;

      if (!manualUrl && messageId) {
        const targetMessage = messages.find((msg) => msg.id === messageId);
        if (targetMessage?.message?.trim()) {
          await generateBufferedAudio(targetMessage.message, messageId);
          manualUrl = manualAudioMapRef.current.get(messageId);
        }
      }

      let needsRefresh = false;

      if (manualUrl && messageId) {
        const pending = pendingStaticAudioRef.current.get(messageId);
        if (pending) {
          pendingStaticAudioRef.current.delete(messageId);
          swapMessageAudioSource(messageId, manualUrl, pending.objectUrl);
        } else {
          swapMessageAudioSource(messageId, manualUrl);
        }

        if (audioElement.src !== manualUrl) {
          audioElement.src = manualUrl;
          audioElement.load();
        }

        needsRefresh = true;
      }

      const ensureFreshElement = async () => {
        if (!needsRefresh) {
          return audioRefs.current.get(key) ?? audioElement;
        }

        await new Promise((resolve) => requestAnimationFrame(() => resolve(undefined)));
        return audioRefs.current.get(key) ?? audioElement;
      };

      const resolvedElement = await ensureFreshElement();
      if (!resolvedElement) {
        toast.info("音频尚未生成或已清理，稍后再试。");
        return;
      }

      let manualEndedHandler: (() => void) | null = null;

      try {
        resolvedElement.pause();
        resolvedElement.currentTime = 0;
        manualEndedHandler = () => {
          resolvedElement.removeEventListener("ended", manualEndedHandler!);
          removePlayingMessage(key);
        };
        resolvedElement.addEventListener("ended", manualEndedHandler, { once: true });
        addPlayingMessage(key);
        await resolvedElement.play();
      } catch (error: any) {
        console.error("[Chat] Manual replay failed:", error);
        if (manualEndedHandler) {
          resolvedElement.removeEventListener("ended", manualEndedHandler);
        }
        removePlayingMessage(key);
        if (error?.name === "NotAllowedError") {
          toast.error("播放失败，浏览器阻止了音频，请检查权限。");
        } else {
          toast.error("无法播放该语音，请稍后再试。");
        }
      }
    },
    [addPlayingMessage, generateBufferedAudio, messages, removePlayingMessage, swapMessageAudioSource],
  );

  const handleMessageSubmit = useCallback(
    async (overrideMessage?: string) => {
      const sourceMessage =
        typeof overrideMessage === "string" ? overrideMessage : input;
      let userMessage = sourceMessage.trim();
      if (!userMessage) {
        return;
      }

      // Note: Vocabulary term selection is now handled by the agent via tool calling
      // The agent will automatically call getVocabularyTermForPractice when needed

      // 检查聊天使用量限制
      if (!usageInfo.chatAvailable) {
        toast.error("聊天功能仅对 Premium 或 Basic Plan Lite 用户开放。请升级以使用此功能。");
        return;
      }

      if (usageInfo.remainingChats <= 0 && usageInfo.chatLimit > 0) {
        const planName = usageInfo.isPremium ? "Premium" : "Basic Plan Lite";
        const limit = usageInfo.chatLimit;
        toast.error(`您已达到今日聊天次数限制（${limit}次/天）。${planName} 用户每天可发送 ${limit} 条消息，请明天再试。`);
        return;
      }

      // 用户点击按钮是一个有效的用户交互，应该能够解锁音频播放
      // 在创建消息之前更新解锁时间戳，确保新消息的时间戳 >= 解锁时间戳
      const newUnlockTimestamp = Date.now();
      
      // 在用户交互时，立即创建一个音频元素并播放静音音频，保持用户交互上下文
      // 这样可以确保后续的音频播放能够使用这个权限
      if (typeof window !== 'undefined') {
        try {
          // 清理之前的预热音频元素
          if (primedAudioElementRef.current) {
            primedAudioElementRef.current.pause();
            primedAudioElementRef.current.src = '';
            primedAudioElementRef.current = null;
          }
          
          // 创建新的音频元素并立即播放静音音频
          const primedAudio = new Audio(SILENT_WAV_DATA_URI);
          primedAudio.muted = true;
          primedAudio.volume = 0;
          primedAudio.play().then(() => {
            primedAudio.pause();
            primedAudio.currentTime = 0;
            primedAudioElementRef.current = primedAudio;
            audioUnlockedRef.current = true;
            console.log('[Chat] Audio permission primed in user interaction context');
          }).catch((error) => {
            console.warn('[Chat] Failed to prime audio in user interaction context:', error);
            primedAudioElementRef.current = null;
          });
        } catch (error) {
          console.warn('[Chat] Failed to create primed audio element:', error);
        }
      }
      
      if (!isAudioEnabled) {
        setIsAudioEnabled(true);
        setAudioUnlockTimestamp(newUnlockTimestamp);
        audioUnlockedRef.current = false; // 重置解锁状态
      } else if (audioUnlockTimestamp === null) {
        // 如果音频已启用但时间戳为 null，更新它
        setAudioUnlockTimestamp(newUnlockTimestamp);
      } else {
        // 即使音频已启用，也更新解锁时间戳以确保新消息可以自动播放
        setAudioUnlockTimestamp(newUnlockTimestamp);
      }

      play();
      setIsSubmit((x) => !x);
      if (!overrideMessage) {
        setInput("");
      }

      // 使用稍后的时间戳确保 generatedAt >= audioUnlockTimestamp
      const messageTimestamp = Date.now();
      const userMessageId = `user-${messageTimestamp}`;
      const assistantMessageId = `ai-${messageTimestamp}`;

      setMessages((prev) => [
        ...prev,
        { id: userMessageId, sentByMe: true, message: sourceMessage.trim(), generatedAt: messageTimestamp },
        { id: assistantMessageId, sentByMe: false, message: "", generatedAt: messageTimestamp },
      ]);

      let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;

      try {
        const history = messages
          .filter((msg) => msg.message && msg.id !== "start")
          .map((msg) => ({
            role: msg.sentByMe ? ("user" as const) : ("assistant" as const),
            content: msg.message,
          }));

        // Debug: Log vocabulary data before sending
        console.log('[PopIsland] Sending chat request:', {
          message: userMessage,
          hasVocabularyData: !!vocabularyData,
          vocabularyScenariosCount: vocabularyData?.scenarios?.length || 0,
        });

        // 获取认证 token
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) {
          toast.error("请先登录以使用聊天功能");
          return;
        }

        const response = await fetch("/api/chat", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            message: userMessage,
            history,
            vocabularyData, // Pass vocabulary data to API so agent can use it via tool
          }),
        });

        // 处理限制错误
        if (response.status === 429) {
          const errorData = await response.json().catch(() => ({}));
          const limit = errorData.limit || usageInfo.chatLimit;
          const planName = usageInfo.isPremium ? "Premium" : "Basic Plan Lite";
          toast.error(`您已达到今日聊天次数限制（${limit}次/天）。${planName} 用户每天可发送 ${limit} 条消息，请明天再试。`);
          
          // 移除已添加的消息
          setMessages((prev) => prev.filter(msg => msg.id !== userMessageId && msg.id !== assistantMessageId));
          
          // 刷新使用量信息
          await refreshUsage();
          return;
        }

        if (!response.ok || !response.body) {
          const errorText = await response.text().catch(() => response.statusText);
          throw new Error(`AI API error: ${errorText || response.statusText}`);
        }

        // 成功发送消息，减少本地计数
        decrementChats();

        reader = response.body.getReader();
        const decoder = new TextDecoder();

        let assistantText = "";
        let bufferedText = "";
        let speechBuffer = "";

        const shouldEndSentence = (char: string, nextChar?: string) => {
          if (!char) {
            return false;
          }
          if ("。！？!?".includes(char) || char === "\n") {
            return true;
          }
          if (char === "." && (!nextChar || nextChar === " ")) {
            return true;
          }
          return false;
        };

        const emitSegments = (isFinal = false) => {
          const segments: string[] = [];
          for (let i = 0; i < speechBuffer.length; i += 1) {
            const char = speechBuffer[i];
            const nextChar = speechBuffer[i + 1];
            if (shouldEndSentence(char, nextChar)) {
              const segment = speechBuffer.slice(0, i + 1).trim();
              if (segment) {
                segments.push(segment);
              }
              speechBuffer = speechBuffer.slice(i + 1).trimStart();
              i = -1;
            }
          }

          if (isFinal && speechBuffer.trim()) {
            segments.push(speechBuffer.trim());
            speechBuffer = "";
          }

          segments.forEach((segment, index) => {
            const isFinalSegment =
              isFinal && index === segments.length - 1 && speechBuffer.length === 0;
            streamTtsForMessage(segment, assistantMessageId, { finalize: isFinalSegment });
          });

          if (isFinal && segments.length === 0 && speechBuffer.length === 0) {
            streamTtsForMessage("", assistantMessageId, { finalize: true });
          }
        };

        const applyAssistantUpdate = () => {
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === assistantMessageId ? { ...msg, message: assistantText } : msg,
            ),
          );
        };

        const processLine = (line: string) => {
          if (!line) {
            return;
          }
          const payload = JSON.parse(line);

          if (payload.type === "delta" && typeof payload.content === "string") {
            assistantText += payload.content;
            speechBuffer += payload.content;
            applyAssistantUpdate();
            emitSegments(false);
          } else if (payload.type === "final" && typeof payload.content === "string") {
            const previous = assistantText;
            assistantText = payload.content;
            if (assistantText.length > previous.length) {
              speechBuffer += assistantText.slice(previous.length);
            }
            applyAssistantUpdate();
          } else if (payload.type === "flashcard" && payload.data) {
            // Handle flashcard data
            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === assistantMessageId
                  ? { ...msg, flashcard: payload.data as Flashcard }
                  : msg
              )
            );
          } else if (payload.type === "error") {
            throw new Error(payload.message || "Stream error");
          }
        };

        while (true) {
          const { value, done } = await reader.read();
          if (value) {
            bufferedText += decoder.decode(value, { stream: !done });
            let newlineIndex;
            while ((newlineIndex = bufferedText.indexOf("\n")) !== -1) {
              const line = bufferedText.slice(0, newlineIndex).trim();
              bufferedText = bufferedText.slice(newlineIndex + 1);
              if (line) {
                processLine(line);
              }
            }
          }
          if (done) {
            break;
          }
        }

        if (bufferedText.trim()) {
          processLine(bufferedText.trim());
        }

        if (!assistantText.trim()) {
          throw new Error("No text in AI response");
        }

        emitSegments(true);
      } catch (error) {
        console.error("Error in handleMessageSubmit:", error);
        toast.error("Failed to get AI response. Please try again.");

        if (reader) {
          try {
            await reader.cancel();
          } catch {
            // ignore cancellation errors
          }
        }

        streamTtsForMessage("", assistantMessageId, { finalize: true });

        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === assistantMessageId
              ? {
                  ...msg,
                  message: "Sorry, I encountered an error. Please try again.",
                }
              : msg,
          ),
        );
      }
    },
    [input, isPremiumUser, messages, play, streamTtsForMessage, vocabularyData, usageInfo, decrementChats, refreshUsage, isAudioEnabled, audioUnlockTimestamp, primeAudioPermission],
  );

  // Start practice directly if initialTerm is provided (after handleMessageSubmit is defined)
  useEffect(() => {
    if (initialTermRef.current && initialPracticeTypeRef.current === 'speaking' && !practiceStartedRef.current) {
      practiceStartedRef.current = true;
      // Start speaking practice directly with the term
      const practiceMessage = `我想练习口语对话，请使用词汇 "${initialTermRef.current}" 进行练习`;
      // Use a small delay to ensure component is fully mounted
      setTimeout(() => {
        handleMessageSubmit(practiceMessage);
      }, 300);
    }
  }, [handleMessageSubmit]);

  const handleRecord = useCallback(async () => {
    console.log("handleRecord called, isRecording:", isRecording);
    if (isRecording && mediaRecorderRef.current) {
      console.log("Stopping recording...");
      mediaRecorderRef.current.stop();
      cleanupMediaRecorder();
    } else {
      console.log("Starting recording...");
      try {
        // Environment and feature checks
        if (typeof window === "undefined") {
          toast.error("Voice recording is only available in the browser.");
      return;
    }

        if (!navigator.mediaDevices?.getUserMedia) {
          toast.error("Voice recording is not supported in this browser.");
      return;
    }

        // Best-effort permissions hint (not supported in all browsers)
        try {
          const permApi: any = (navigator as any).permissions;
          if (permApi?.query) {
            const status = await permApi.query({ name: "microphone" as any });
            if (status?.state === "denied") {
              toast.error(
                "Microphone access is denied. Enable it in your browser settings.",
              );
              return;
            }
          }
        } catch {
          // Ignore permissions API errors; proceed to request directly
        }

        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });

        // Pick a supported MIME type to maximize cross-browser compatibility (e.g., Safari)
        const candidateMimeTypes = [
          "audio/webm;codecs=opus",
          "audio/webm",
          "audio/mp4;codecs=mp4a.40.2",
          "audio/mp4",
          "audio/ogg;codecs=opus",
          "audio/mpeg",
        ];
        const isTypeSupported = (type: string) =>
          typeof MediaRecorder !== "undefined" &&
          (MediaRecorder as any).isTypeSupported?.(type);
        const selectedMimeType = candidateMimeTypes.find((t) =>
          isTypeSupported(t),
        );

        let recorder: MediaRecorder;
        try {
          recorder = selectedMimeType
            ? new MediaRecorder(stream, { mimeType: selectedMimeType })
            : new MediaRecorder(stream);
        } catch (e) {
          // Fallback: try without options
          recorder = new MediaRecorder(stream);
        }
        mediaRecorderRef.current = recorder;

        recorder.addEventListener("dataavailable", async (event) => {
          if (event.data.size > 0) {
            const audioBlob = event.data;

            try {
              const formData = new FormData();
              const extension = (() => {
                const type = (audioBlob?.type || "").toLowerCase();
                if (type.includes("mp4")) return "mp4";
                if (type.includes("ogg")) return "ogg";
                if (type.includes("mpeg")) return "mp3";
                return "webm";
              })();
              formData.append("audio", audioBlob, `recording.${extension}`);
              const response = await fetch("/api/transcribe-groq", {
                method: "POST",
                body: formData,
              });

              if (!response.ok) {
                throw new Error(`Transcription failed: ${response.statusText}`);
              }

              const data = await response.json();

              if (data.text) {
                await handleMessageSubmit(data.text);
              } else {
                console.error("Transcription response did not contain text:", data);
              }
            } catch (error) {
              console.error("Error during transcription request:", error);
              toast.error("Failed to transcribe audio. Please try again.");
            } finally {
              cleanupMediaRecorder();
            }
          }
        });

        recorder.addEventListener("error", (e) => {
          console.error("MediaRecorder error:", e);
          toast.error("Recording failed. Please try again or switch browser.");
          cleanupMediaRecorder();
        });

        recorder.addEventListener("stop", () => {
          stream.getTracks().forEach((track) => track.stop());
        });

        recorder.start();
        setIsRecording(true);
        audioLinesRef.current?.startAnimation();
      } catch (error) {
        console.error("Error accessing microphone:", error);
        toast.error("Could not access microphone. Please allow mic permission.");
        setIsRecording(false);
      }
    }
  }, [isRecording, cleanupMediaRecorder, handleMessageSubmit]);

  return (
    <div className="relative flex h-full w-full flex-col items-center justify-center overflow-hidden bg-[#F5F6F6] p-2 font-medium text-black shadow-[0_10px_20px_-6px_rgba(0,0,0,0.1)]">
      {/* Main input container */}
      <div className="w-full max-w-lg">
        <div className="relative flex flex-col rounded-3xl bg-[#F5F6F6] p-0">
          <div className="relative flex items-center justify-start">
            <button
              ref={settingsButtonRef}
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                setIsSettingsMenuOpen((prev) => !prev);
              }}
              onPointerDown={(event) => event.stopPropagation()}
              className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 shadow-[0_8px_18px_-14px_rgba(15,23,42,0.45)] transition hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-gray-900/20"
            >
              <Settings2 className="h-4 w-4" />
              {/* <span>设置</span> */}
            </button>
            {isSettingsMenuOpen ? (
              <div
                ref={settingsMenuRef}
                onPointerDown={(event) => event.stopPropagation()}
                className="absolute left-0 top-full z-20 mt-1 w-40 rounded-2xl border border-gray-200 bg-white p-3 shadow-[0_20px_45px_-24px_rgba(15,23,42,0.65)]"
              >
                {/* <div className="pb-2 text-sm font-semibold text-muted-foreground">聊天首选项</div> */}
                <div className="rounded-xl">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="text-sm font-medium text-foreground">语音朗读</p>
                      {/* <p className="text-xs text-muted-foreground">自动播放 AI 回复</p> */}
                    </div>
                    <Switch
                      className="data-[state=checked]:bg-black data-[state=unchecked]:bg-muted"
                      checked={isAudioEnabled}
                      onCheckedChange={() => handleToggleAudioPlayback()}
                      aria-label="切换语音朗读"
                    />
                  </div>
                </div>
              </div>
            ) : null}
          </div>
          {/* Messages container with fixed height and scroll */}
          <div
            ref={messagesContainerRef}
            className={cn([
              "relative flex flex-col overflow-y-auto overflow-x-hidden",
              "w-full",
              "px-1 pt-2 pb-2",
              "pointer-events-auto",
              "flex-shrink-0",
              "min-h-0",
              "gap-3",
              messages.length === 0 ? "h-[100px]" : "min-h-[100px] max-h-[300px]",
            ])}
            style={{
              scrollBehavior: "smooth",
            }}
          >
            {messages.map((item, index) => {
              const messageKey = item.id ?? `idx-${index}`;
              const showTranslationButton = !item.sentByMe && Boolean(item.message?.trim());
              const showAudioButton = !item.sentByMe && Boolean(item.audioUrl);
              const translationInfo = translationState[messageKey];
              const isMessagePlaying = messageKey ? playingMessageIds.has(messageKey) : false;

              if (item.sentByMe) {
                return (
                  <div key={messageKey} className="flex flex-col items-end w-full">
                    <motion.div
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.2, type: "spring", bounce: 0.4 }}
                      className={cn([
                        "relative",
                        "max-w-[260px]",
                        "break-words px-[12px] py-[8px]",
                        "text-sm",
                        "flex-shrink-0",
                        "whitespace-normal",
                        "flex flex-col gap-1",
                        "self-end rounded-[14px_14px_6px] bg-white text-black shadow-[0_10px_20px_-6px_rgba(0,0,0,0.1)]",
                      ])}
                    >
                      <div className="will-change-transform break-words">
                        <MemoizedMarkdown
                          content={item.message}
                          id={`${messageKey}-markdown`}
                          className="prose prose-sm max-w-none break-words text-left text-black marker:text-current prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0 prose-headings:mb-1 prose-headings:text-base prose-strong:font-semibold"
                        />
                      </div>
                    </motion.div>
                  </div>
                );
              }

              return (
                <div key={messageKey} className="flex flex-col items-start gap-2 z-10 w-full">
                  <div className="flex items-end gap-2">
                    <motion.div
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.2, type: "spring", bounce: 0.4 }}
                      className={cn([
                        "relative",
                        "max-w-[260px]",
                        "break-words px-[12px] py-[8px]",
                        "text-sm",
                        "flex-shrink-0",
                        "whitespace-normal",
                        "flex flex-col gap-1",
                        "self-start rounded-[14px_14px_14px_6px] bg-sky-500 text-white",
                        showAudioButton ? "pr-2" : "pr-2",
                      ])}
                    >
                      <div className="will-change-transform break-words">
                        {!item.message || item.message.trim() === '' ? (
                          <div className="flex items-center justify-start py-1">
                            <div className="flex items-center space-x-1 h-4">
                              {[...Array(3)].map((_, i) => (
                                <div
                                  key={i}
                                  className="bg-white animate-[bounce-dots_1.4s_ease-in-out_infinite] rounded-full h-1.5 w-1.5"
                                  style={{
                                    animationDelay: `${i * 160}ms`,
                                  }}
                                />
                              ))}
                            </div>
                          </div>
                        ) : messageKey === 'start' ? (
                          <TextGenerateEffect
                            words={item.message || ''}
                            className="prose prose-sm prose-invert max-w-none break-words text-left text-white marker:text-current prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0 prose-headings:mb-1 prose-headings:text-base prose-strong:font-semibold"
                            wordClassName="text-white"
                            duration={0.5}
                            staggerDelay={0.05}
                          />
                        ) : (
                          <MemoizedMarkdown
                            content={item.message}
                            id={`${messageKey}-markdown`}
                            className="prose prose-sm prose-invert max-w-none break-words text-left text-white marker:text-current prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0 prose-headings:mb-1 prose-headings:text-base prose-strong:font-semibold"
                          />
                        )}
                      </div>
                      {showAudioButton ? (
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            void handleManualReplay(item.id ?? messageKey);
                          }}
                          className="z-20 absolute right-2 -bottom-1 inline-flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full bg-white/50 text-sky-500 transition-colors hover:bg-white/90"
                          aria-label={isMessagePlaying ? "暂停语音" : "播放语音"}
                        >
                          {isMessagePlaying ? (
                            <Pause className="h-4 w-4" />
                          ) : (
                            <PlayTriangleIcon className="h-4 w-4" />
                          )}
                        </button>
                      ) : null}
                      {translationInfo?.text ? (
                        <p className="text-[11px] leading-relaxed text-white/95">
                          {translationInfo.text}
                        </p>
                      ) : null}
                      {translationInfo?.error && !translationInfo?.text ? (
                        <p className="mt-1 text-[11px] text-red-100">{translationInfo.error}</p>
                      ) : null}
                      {item.audioUrl && (
                        <audio
                          ref={(el) => {
                            if (!messageKey) {
                              return;
                            }
                            if (el) {
                              audioRefs.current.set(messageKey, el);
                            } else {
                              console.log(`[Chat] Audio element unmounted for message ${messageKey}`);
                              audioRefs.current.delete(messageKey);
                            }
                          }}
                          src={item.audioUrl}
                          className="hidden"
                          preload="auto"
                          onLoadedData={(e) => {
                            console.log(`[Chat] Audio loaded for message ${index}`, {
                              duration: e.currentTarget.duration,
                              readyState: e.currentTarget.readyState,
                            });
                            // 当音频加载完成时，如果它在播放队列中且还没播放，尝试播放
                            const messageKey = item.id ?? `idx-${index}`;
                            if (messageKey && 
                                playbackQueueRef.current.includes(messageKey) && 
                                !isPlayingRef.current &&
                                isAudioEnabled) {
                              console.log('[Chat] Audio loaded, triggering playback retry:', messageKey);
                              // 延迟一点，确保音频完全准备好
                              setTimeout(() => {
                                if (playbackQueueRef.current.includes(messageKey) && !isPlayingRef.current) {
                                  playNextAudioInQueue();
                                }
                              }, 100);
                            }
                          }}
                          onError={(e) => {
                            console.error(`[Chat] Audio load error for message ${index}:`, e);
                          }}
                        />
                      )}
                    </motion.div>
                    {showTranslationButton ? (
                      <button
                        type="button"
                        disabled={translationInfo?.loading}
                        onClick={(event) => {
                          event.stopPropagation();
                          void handleTranslateMessage(item.id ?? messageKey, item.message);
                        }}
                        className="inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-xl border border-black/30 bg-white text-black shadow-[0_10px_24px_-16px_rgba(15,23,42,0.6)] transition hover:bg-white/90 disabled:opacity-60"
                        aria-label="翻译成中文"
                      >
                        {translationInfo?.loading ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Languages className="h-4 w-4" />
                        )}
                      </button>
                    ) : null}
                  </div>
                  {item.flashcard ? (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9, y: 20 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      transition={{ duration: 0.4, type: "spring", bounce: 0.3, delay: 0.2 }}
                      className="w-full"
                    >
                      <FlashcardComponent flashcard={item.flashcard} />
                    </motion.div>
                  ) : null}
                </div>
              );
            })}
            <div ref={messagesEndRef} className="flex-shrink-0" />
            </div>
          {showQuickChatActions ? (
            <div className="mb-1.5 flex flex-wrap gap-2 px-1">
              {quickChatActions.map((action) => {
                const Icon = action.icon;
                const accentClassName = action.accentClassName ?? "text-black";
                return (
                  <button
                    key={action.label}
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      void handleMessageSubmit(action.prompt);
                    }}
                    className="inline-flex font-sans font-[500] bg-white border border-black/10 items-center gap-2 rounded-full px-3 py-1 text-[12px] text-black shadow-sm transition hover:bg-black/5"
                  >
                    {Icon ? (
                      <Icon className={cn("h-4 w-4", accentClassName)} />
                    ) : (
                      <ArrowRight className="h-3 w-3 text-black" />
                    )}
                    <span className="text-black">{action.label}</span>
                  </button>
                );
              })}
            </div>
          ) : null}
          <div className="relative z-10 flex items-center gap-1.5 border border-border rounded-2xl border-gray-100 bg-white p-1.5">
            {/* SVG Avatar Button */}
            <button
              type="button"
              className="flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-xl transition-colors"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 256 256"
                fill="none"
                role="img"
                aria-hidden="true"
                className="w-full h-full"
                style={{ 
                  transition: 'transform 0.8s ease-in-out',
                  transformOrigin: 'center center'
                }}
              >
                <circle cx="128" cy="128" r="128" fill="#F97316"></circle>
                <ellipse 
                  cx="100.5546299346353" 
                  cy="140.5667763724447" 
                  rx="18" 
                  ry="18" 
                  fill="#fff" 
                  className="face-icon-blink"
                ></ellipse>
                <ellipse 
                  cx="147.3546299346353" 
                  cy="140.5667763724447" 
                  rx="18" 
                  ry="18" 
                  fill="#fff" 
                  className="face-icon-blink"
                ></ellipse>
              </svg>
            </button>
            {/* Voice Recording Button */}
            <button
              type="button"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                console.log("AudioLines button clicked, isSubmit:", isSubmit);
                handleRecord();
              }}
              className={cn(
                "relative z-10 flex flex-1 items-center justify-center gap-2 overflow-hidden rounded-xl transition-colors cursor-pointer h-10",
                isRecording
                  ? "bg-red-500 hover:bg-red-600"
                  : "bg-[#121212] hover:bg-[#1a1a1a]",
              )}
            >
              <AudioLinesIcon
                ref={audioLinesRef}
                size={20}
                className="text-white"
              />
              <span className="text-white text-sm font-medium">Click to record</span>
            </button>
      </div>
        </div>
      </div>

      {/* <motion.div
        key={`isSubmit${isSubmit}`}
        initial={{
          y: "100%",
          opacity: 0.2,
        }}
        transition={{
          type: "spring",
          stiffness: 100,
          damping: 20,
        }}
        animate={{
          y: "30%",
          opacity: 0,
        }}
        className="z-1 absolute left-1/2 flex h-[100vh] w-full max-w-3xl -translate-x-1/2"
      >
        <Grad className="w-full" />
        <Grad className="w-full -translate-y-20" />
        <Grad className="w-full" />
      </motion.div> */}
    </div>
  );
}

// Email OTP View
function EmailOTPView({ setView }: { setView: (view: string) => void }) {
  return (
    <div>
      <div className="flex items-center justify-between gap-3 px-6 py-6 text-center text-xl font-semibold tracking-tight sm:font-medium">
        <button
          onClick={() => setView("signin")}
          className="flex h-8 w-8 items-center justify-center rounded-full border border-[#eceef2] bg-white text-[#7b7f88] shadow-[0_8px_18px_-14px_rgba(15,23,42,0.45)] transition-transform focus:scale-95 active:scale-90"
        >
          <ChevronLeft className="opacity-45" />
        </button>
        <h2 className="select-none">Confirm Email</h2>
        <span className="size-8 opacity-0" />
      </div>

      <div className="space-y-6 px-6 text-center">
        <div>
          <p className="text-muted-foreground">
            Enter the verification code sent to
          </p>
          <h1 className="font-medium tracking-tight">yo@guri.in</h1>
        </div>

        <div className="space-y-4">
          <InputOTP
            autoFocus
            onKeyDown={(e:any) => {
              if (e.key === "Enter") {
                setView("signin");
              }
            }}
            maxLength={6}
            className="flex justify-center gap-3"
          >
            <InputOTPSlot className="h-12 w-12" index={0} />
            <InputOTPSlot className="h-12 w-12" index={1} />
            <InputOTPSlot className="h-12 w-12" index={2} />
            <InputOTPSlot className="h-12 w-12" index={3} />
            <InputOTPSlot className="h-12 w-12" index={4} />
            <InputOTPSlot className="h-12 w-12" index={5} />
          </InputOTP>

          <button
            onClick={() => setView("signin")}
            className="mb-6 w-full rounded-full bg-green-500 py-3 font-semibold text-white transition-colors"
          >
            Verify Code
          </button>
        </div>
      </div>
    </div>
  );
}

// Phone OTP View
function PhoneOTPView({ setView }: { setView: (view: string) => void }) {
  return (
    <div>
      <div className="flex items-center justify-between gap-3 px-6 py-6 text-center text-xl font-semibold tracking-tight sm:font-medium">
        <button
          onClick={() => setView("signin")}
          className="flex h-8 w-8 items-center justify-center rounded-full border border-[#eceef2] bg-white text-[#7b7f88] shadow-[0_8px_18px_-14px_rgba(15,23,42,0.45)] transition-transform focus:scale-95 active:scale-90"
        >
          <ChevronLeft className="opacity-45" />
        </button>
        <h2 className="select-none">Confirm Phone</h2>
        <span className="size-8 opacity-0" />
      </div>

      <div className="space-y-6 px-6 text-center">
        <div>
          <p className="text-muted-foreground">
            Enter the verification code sent to
          </p>
          <h1 className="font-medium tracking-tight">+1 (555) 123-4567</h1>
        </div>

        <div className="space-y-4">
          <InputOTP
            autoFocus
            onKeyDown={(e:any) => {
              if (e.key === "Enter") {
                setView("signin");
              }
            }}
            maxLength={6}
            className="flex justify-center gap-3"
          >
            <InputOTPSlot className="h-12 w-12" index={0} />
            <InputOTPSlot className="h-12 w-12" index={1} />
            <InputOTPSlot className="h-12 w-12" index={2} />
            <InputOTPSlot className="h-12 w-12" index={3} />
            <InputOTPSlot className="h-12 w-12" index={4} />
            <InputOTPSlot className="h-12 w-12" index={5} />
          </InputOTP>

          <button
            onClick={() => setView("signin")}
            className="mb-6 w-full rounded-full bg-green-500 py-3 font-semibold text-white transition-colors"
          >
            Verify Code
          </button>
        </div>
      </div>
    </div>
  );
}

// Passkey View
function PasskeyView({ setView }: { setView: (view: string) => void }) {
  return (
    <div>
      <div className="flex items-center justify-between gap-3 px-6 py-6 text-center text-xl font-semibold tracking-tight sm:font-medium">
        <button
          onClick={() => setView("signin")}
          className="flex h-8 w-8 items-center justify-center rounded-full border border-[#eceef2] bg-white text-[#7b7f88] shadow-[0_8px_18px_-14px_rgba(15,23,42,0.45)] transition-transform focus:scale-95 active:scale-90"
        >
          <ChevronLeft className="opacity-45" />
        </button>
        <h2 className="select-none">Passkey</h2>
        <span className="size-8 opacity-0" />
      </div>

      <div className="flex flex-col items-center justify-center space-y-6 px-6 text-center">
        <Button
          duration={1500}
          borderRadius="30px"
          containerClassName="p-[6px] "
          className="flex size-20 items-center justify-center !rounded-[24px] border border-[#e4e7ed] bg-white text-[#111827] shadow-[0_24px_48px_-36px_rgba(15,23,42,0.7)]"
        >
          <Fingerprint className="size-8 opacity-45" />
        </Button>
        <div>
          <h1 className="text-xl font-medium tracking-tight">
            Waiting for passkey
          </h1>
          <p className="text-muted-foreground max-w-2xs mt-1 text-pretty text-sm">
            Please follow prompts to verify your passkey.
          </p>
        </div>

        <button
          onClick={() => setView("signin")}
          className="mb-6 w-full rounded-full bg-[#4EAFFF] py-3 font-semibold text-white transition-colors"
        >
          Continue
        </button>
      </div>
    </div>
  );
}

// Wallet View
function WalletView({ setView }: { setView: (view: string) => void }) {
  return (
    <div>
      <div className="flex items-center justify-between gap-3 px-6 py-6 text-center text-xl font-semibold tracking-tight sm:font-medium">
        <button
          onClick={() => setView("signin")}
          className="flex h-8 w-8 items-center justify-center rounded-full border border-[#eceef2] bg-white text-[#7b7f88] shadow-[0_8px_18px_-14px_rgba(15,23,42,0.45)] transition-transform focus:scale-95 active:scale-90"
        >
          <ChevronLeft className="opacity-45" />
        </button>
        <h2 className="select-none">Connect Wallet</h2>
        <span className="size-8 opacity-0" />
      </div>

      <div className="space-y-2 px-6 pb-6 text-center">
        {walletProviders.map(({ label, icon: Icon }) => (
          <div
            key={label}
            onClick={() => setView("signin")}
            className="flex h-15 cursor-pointer items-center justify-between rounded-2xl border border-[#e4e7ed] bg-white px-4 py-3 shadow-[0_20px_40px_-32px_rgba(15,23,42,0.7)] transition-colors hover:border-[#dbe0ea]"
          >
            <h1 className="text-lg font-medium tracking-tight">{label}</h1>
            <Icon className="size-6" />
          </div>
        ))}
        <div
          onClick={() => setView("signin")}
          className="flex h-15 cursor-pointer items-center justify-between rounded-2xl border border-[#e4e7ed] bg-white px-4 py-3 shadow-[0_20px_40px_-32px_rgba(15,23,42,0.7)] transition-colors hover:border-[#dbe0ea]"
        >
          <div className="flex items-center justify-start gap-3">
            <span className="text-center text-lg font-medium">
              Other Wallets
            </span>
            <div className="flex select-none items-center justify-center rounded-full border border-[#e4e7ed] bg-white px-2 py-1 text-xs font-semibold text-[#7a7f87] sm:font-medium">
              350+
            </div>
          </div>
          <div className="flex size-9 select-none items-center justify-center rounded-lg border border-[#e4e7ed] bg-white text-xs font-semibold text-[#7a7f87] sm:font-medium">
            <Wallet className="size-5" />
          </div>
        </div>
        <button
          onClick={() => setView("signin")}
          className="hover:text-foreground text-muted-foreground flex w-full items-center justify-center gap-2 pb-3 pt-6 font-semibold sm:font-medium"
        >
          <Wallet className="size-5" />
          I Don't Have a Wallet
        </button>
      </div>
    </div>
  );
}

export function Button({
  borderRadius = "1.75rem",
  children,
  as: Component = "button",
  containerClassName,
  borderClassName,
  duration,
  className,
  ...otherProps
}: {
  borderRadius?: string;
  children: React.ReactNode;
  as?: any;
  containerClassName?: string;
  borderClassName?: string;
  duration?: number;
  className?: string;
  [key: string]: any;
}) {
  return (
    <Component
      className={cn(
        "relative overflow-hidden bg-transparent p-[1px] text-xl",
        containerClassName,
      )}
      style={{
        borderRadius: borderRadius,
      }}
      {...otherProps}
    >
      <div
        className="absolute inset-0"
        style={{ borderRadius: `calc(${borderRadius} * 0.96)` }}
      >
        <MovingBorder duration={duration} rx="30%" ry="30%">
          <div
            className={cn(
              "h-20 w-20 bg-[radial-gradient(#0ea5e9_40%,transparent_60%)] opacity-[0.8]",
              borderClassName,
            )}
          />
        </MovingBorder>
      </div>

      <div
        className={cn(
          "relative flex h-full w-full items-center justify-center border border-slate-800 bg-slate-900/[0.8] text-sm text-white antialiased backdrop-blur-xl",
          className,
        )}
        style={{
          borderRadius: `calc(${borderRadius} * 0.96)`,
        }}
      >
        {children}
      </div>
    </Component>
  );
}

export const MovingBorder = ({
  children,
  duration = 3000,
  rx,
  ry,
  ...otherProps
}: {
  children: React.ReactNode;
  duration?: number;
  rx?: string;
  ry?: string;
  [key: string]: any;
}) => {
  const pathRef = useRef<any>(null);
  const progress = useMotionValue<number>(0);

  useAnimationFrame((time) => {
    const length = pathRef.current?.getTotalLength();
    if (length) {
      const pxPerMillisecond = length / duration;
      progress.set((time * pxPerMillisecond) % length);
    }
  });

  const x = useTransform(
    progress,
    (val) => pathRef.current?.getPointAtLength(val).x,
  );
  const y = useTransform(
    progress,
    (val) => pathRef.current?.getPointAtLength(val).y,
  );

  const transform = useMotionTemplate`translateX(${x}px) translateY(${y}px) translateX(-50%) translateY(-50%)`;

  return (
    <>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        preserveAspectRatio="none"
        className="absolute h-full w-full"
        width="100%"
        height="100%"
        {...otherProps}
      >
        <rect
          fill="none"
          width="100%"
          height="100%"
          rx={rx}
          ry={ry}
          ref={pathRef}
        />
      </svg>
      <motion.div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          display: "inline-block",
          transform,
        }}
      >
        {children}
      </motion.div>
    </>
  );
};
